import { Box, Divider, Grid, Hidden } from '@mui/material';
import {
  GenButton,
  GenUnitCard,
  CollapsibleTable,
  GenSnackBar,
} from '@pinnacleautomotive/clarity-components';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import carPlaceholder from '../../../assets/carPlaceholder.png';
import Empty from '../../../assets/emptyUnapproved.png';
import RequesterDetails from '../../../components/RequesterDetails';
import ServicesDetails from '../../../components/ServicesDetails';
import {
  approveOrder,
  declineOrder,
  getUnapprovedOrders,
  closeApproveDialog,
  closeDeclineDialog,
  openDeclineDialog,
  clearDeclinedConfirmation,
} from '../../../redux/actions/orders/unapprovedActions';
import {
  selectOrdersUnapproved,
  selectHasMore,
  selectIsLoading,
  selectCurrentPage,
  selectProcessingAction,
  selectDeclineDialogOpened,
  selectApproveDialogOpened,
  selectShowDeclinedSuccesSnackBar,
  selectShowDeclinedErrorSnackBar,
} from '../../../redux/selectors/orders/unapprovedSelectors';
import { UnapprovedColumns } from '../commons';
import EmptyState from '../components/EmptyState';
import OrderConfirmDialog from '../components/OrderConfirmDialog';
import OrderDeclineDialog from '../components/OrderDeclineDialog';

const Unapproved = ({ searchQuery, companyId, departments }) => {
  const [orderSelected, setOrderSelected] = useState(null);
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation('orders');
  const orders = useSelector(selectOrdersUnapproved);
  const hasMore = useSelector(selectHasMore);
  const loading = useSelector(selectIsLoading);
  const processingAction = useSelector(selectProcessingAction);
  const declineDialogOpened = useSelector(selectDeclineDialogOpened);
  const approveDialogOpened = useSelector(selectApproveDialogOpened);
  const showDeclinedSuccesSnackBar = useSelector(
    selectShowDeclinedSuccesSnackBar
  );
  const showDeclinedErrorSnackBar = useSelector(
    selectShowDeclinedErrorSnackBar
  );
  const currentPage = useSelector(selectCurrentPage);

  useEffect(() => {
    dispatch(
      getUnapprovedOrders({
        companyId,
        departments,
        pageNumber: 1,
        searchQuery,
      })
    );
  }, [companyId, departments, dispatch, searchQuery]);

  const loadMoreData = () => {
    dispatch(
      getUnapprovedOrders({
        companyId,
        departments,
        pageNumber: currentPage + 1,
        searchQuery,
      })
    );
  };

  const dispatchDeclineOrder = observation => {
    dispatch(
      declineOrder({
        order: orderSelected,
        observation,
        searchQuery,
        companyId,
        departments,
      })
    );
  };

  const navigateToWorkQueue = () => {
    history.push('/workQueue');
  };

  const columns = UnapprovedColumns(t, [
    {
      valueGetter: data => (
        <Grid container spacing={2}>
          <Grid item>
            <GenButton
              color="error"
              variant="outlined"
              disabled={
                orderSelected?.vendorOrderId === data.vendorOrderId &&
                processingAction
              }
              text={t('actionButtons.decline')}
              onClick={event => {
                event.stopPropagation();
                setOrderSelected(data);
                dispatch(openDeclineDialog());
              }}
            />
          </Grid>
          <Grid item>
            <GenButton
              color="success"
              variant="outlined"
              disabled={
                orderSelected?.vendorOrderId === data.vendorOrderId &&
                processingAction
              }
              text={t('actionButtons.approve')}
              onClick={event => {
                event.stopPropagation();
                setOrderSelected(data);
                dispatch(
                  approveOrder({
                    order: data,
                    searchQuery,
                    companyId,
                    departments,
                  })
                );
              }}
            />
          </Grid>
        </Grid>
      ),
    },
  ]);

  const hideDeclineModal = () => {
    setOrderSelected(null);
    dispatch(closeDeclineDialog());
  };

  const hideDeclinedConfirmation = () => {
    dispatch(clearDeclinedConfirmation());
  };

  return (
    <>
      <OrderConfirmDialog
        onClose={() => dispatch(closeApproveDialog())}
        open={approveDialogOpened}
        order={orderSelected}
        processingAction={processingAction}
        onConfirm={() => navigateToWorkQueue()}
      />
      <OrderDeclineDialog
        onClose={() => hideDeclineModal()}
        open={declineDialogOpened}
        order={orderSelected}
        processingAction={processingAction}
        onConfirm={observation => dispatchDeclineOrder(observation)}
        onSuccess={showDeclinedSuccesSnackBar}
      />
      <GenSnackBar
        title={t('declinedConfirmation')}
        open={showDeclinedSuccesSnackBar}
        onClose={() => hideDeclinedConfirmation()}
      />
      <GenSnackBar
        title={t('declinedError')}
        open={showDeclinedErrorSnackBar}
        type="error"
        onClose={() => hideDeclinedConfirmation()}
      />
      <CollapsibleTable
        columns={columns}
        rows={orders}
        hasNextPage={hasMore}
        loading={loading}
        onLoadMore={() => loadMoreData()}
        onClick={workOrder =>
          history.push({
            pathname: `/orders/${workOrder.vendorOrderId}`,
          })
        }
        collpasibleGetter={data => (
          <GenUnitCard
            order={data}
            image={carPlaceholder}
            showStockAndWoNumber={false}
          >
            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }}>
              <ServicesDetails data={data} />
              <Hidden mdUp>
                <Divider />
              </Hidden>
              <RequesterDetails data={data} />
            </Box>
          </GenUnitCard>
        )}
      />
      <EmptyState
        show={!loading && !orders.length}
        img={Empty}
        title={t('global:noWOUnapproveTitle')}
        subtitle={t('global:noWOUnapprove')}
      />
    </>
  );
};

export default Unapproved;
